import { useCallback, useEffect, useMemo, useState } from 'react';
import { instance } from '../../axios/AxiosInstance';
import type { ListResponse } from '../Settings/lists/singleTableListSupport';
import { settingsListApiError } from '../Settings/lists/singleTableListSupport';
import { fetchBooks, type BookListItem } from '../Books/bookApi';

const PATH = 'authors';

function bookCountByAuthorIdFromBooks(books: BookListItem[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const b of books) {
    const k = String(b.author_id);
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}

export interface LoadAuthorsOptions {
  /** When true, skip full-panel loading state (e.g. after save or delete). */
  silent?: boolean;
}

export interface UseAuthorsListOptions {
  withBookCounts?: boolean;
}

export function useAuthorsList<T>(options?: UseAuthorsListOptions) {
  const withBookCounts = options?.withBookCounts !== false;
  const [rows, setRows] = useState<T[]>([]);
  const [books, setBooks] = useState<BookListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const bookCountByAuthorId = useMemo(() => bookCountByAuthorIdFromBooks(books), [books]);

  const load = useCallback(async (options?: LoadAuthorsOptions) => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [{ data }, bookList] = await Promise.all([
        instance.get<ListResponse>(PATH),
        withBookCounts ? fetchBooks() : Promise.resolve([]),
      ]);
      setRows(Array.isArray(data.data) ? (data.data as T[]) : []);
      setBooks(bookList);
    } catch (e: unknown) {
      if (!silent) {
        setError(
          settingsListApiError(e, 'Зохиолч эсвэл номуудын жагсаалт ачааллахад алдаа гарлаа.'),
        );
        setRows([]);
        setBooks([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [withBookCounts]);

  useEffect(() => {
    void load();
  }, [load]);

  const deleteById = useCallback(
    async (id: string) => {
      setDeletingId(id);
      setError(null);
      try {
        await instance.delete(PATH, { data: { id } });
        await load({ silent: true });
      } catch (e: unknown) {
        setError(settingsListApiError(e, 'Устгахад алдаа гарлаа.'));
      } finally {
        setDeletingId(null);
      }
    },
    [load],
  );

  return { rows, loading, error, load, deleteById, deletingId, bookCountByAuthorId };
}
